import {useRef, useCallback, useEffect} from 'react'
import useAnimationFrameCallback from '../leap/hooks/useAnimationFrameCallback'

/**
 * Hook to hide scrollbar on initial render for overlay scrollbar environments (mobile Chrome).
 * The scrollbar is shown again after the first scroll event.
 *
 * This hook works around a mobile Chrome bug where overlay scrollbars are visible on initial
 * render and don't auto-hide until the user scrolls. It detects overlay scrollbar environments
 * using the `(pointer: coarse)` media query and adds a `data-hide-scrollbar` attribute.
 *
 * Note: Requires global CSS with `[data-hide-scrollbar] { scrollbar-width: none; }` etc.
 *
 * @returns {Function} Callback ref to attach to the scrollable element
 *
 * @example
 * // Basic usage
 * const scrollbarRef = useHideInitialScrollbar()
 * return <div ref={scrollbarRef} style={{overflowX: 'auto'}}>...</div>
 *
 * @example
 * // With Chakra UI SegmentGroup
 * const scrollbarRef = useHideInitialScrollbar()
 * return <SegmentGroup.Root ref={scrollbarRef}>...</SegmentGroup.Root>
 */
const useHideInitialScrollbar = () => {
    const cleanupRef = useRef(null)

    // Use RAF callback for smooth DOM updates
    const removeAttributeOnRaf = useAnimationFrameCallback((element) => {
        element.removeAttribute('data-hide-scrollbar')
    })

    const callbackRef = useCallback(
        (element) => {
            // Cleanup previous element if any
            if (cleanupRef.current) {
                cleanupRef.current()
                cleanupRef.current = null
            }

            if (!element) return

            // Check if we're in an overlay scrollbar environment
            const hasOverlayScrollbar = window.matchMedia('(pointer: coarse)').matches

            if (!hasOverlayScrollbar) return

            // Add attribute to hide scrollbar
            element.setAttribute('data-hide-scrollbar', 'true')

            // Create scroll handler to remove hiding on first scroll
            const handleScroll = () => {
                // Use RAF to ensure DOM modification happens in sync with rendering
                removeAttributeOnRaf(element)
            }

            // Create AbortController for listener cleanup
            const abortController = new AbortController()

            element.addEventListener('scroll', handleScroll, {
                passive: true,
                once: true,
                signal: abortController.signal
            })

            // Store cleanup function
            cleanupRef.current = () => {
                // Abort the event listener (works even if 'once' already removed it)
                abortController.abort()
                // Remove attribute immediately (no RAF needed for cleanup)
                element.removeAttribute('data-hide-scrollbar')
            }
        },
        [removeAttributeOnRaf]
    )

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (cleanupRef.current) {
                cleanupRef.current()
                cleanupRef.current = null
            }
        }
    }, [])

    return callbackRef
}

export default useHideInitialScrollbar
